import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Sparkle, X } from "@phosphor-icons/react";
import api from "../lib/api";

async function compressImage(file, maxSize = 1280, quality = 0.78) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          const ratio = Math.min(maxSize / width, maxSize / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const c = document.createElement("canvas");
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// Reusable photo + AI analyze block. `endpoint` defaults to /products/analyze-photo
// but callers (Live quick-add) can override to /orders/analyze-photo.
export default function PhotoAIField({
  photoUrl, onPhoto, onExtract,
  endpoint = "/products/analyze-photo",
  testidPrefix = "product",
  compact = false,
}) {
  const [analyzing, setAnalyzing] = useState(false);
  const inputRef = useRef(null);

  const onPickFile = async (file) => {
    if (!file) return;
    try {
      const dataUrl = await compressImage(file);
      onPhoto(dataUrl);
    } catch { toast.error("Falha ao ler a imagem"); }
  };

  const analyze = async () => {
    if (!photoUrl) return toast.error("Envie uma foto primeiro");
    setAnalyzing(true);
    try {
      const { data } = await api.post(endpoint, { image_base64: photoUrl });
      onExtract(data);
      toast.success("Cadastro preenchido pela IA · revise antes de salvar");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao analisar foto");
    } finally { setAnalyzing(false); }
  };

  const size = compact ? "w-16 h-16" : "w-24 h-24";
  const iconSize = compact ? 24 : 32;
  const btnHeight = compact ? "h-[40px]" : "h-[44px]";

  return (
    <div className="card-brutal p-3 bg-brand-cream flex flex-wrap items-start gap-3">
      <div className={`${size} border-2 border-brand-ink bg-white flex items-center justify-center overflow-hidden shrink-0`}>
        {photoUrl
          ? <img data-testid={`${testidPrefix}-photo-preview`} src={photoUrl} alt="Foto" className="w-full h-full object-cover" />
          : <Camera weight="bold" size={iconSize} className="text-brand-ink2" />}
      </div>
      <div className="flex-1 min-w-[180px] space-y-1.5">
        {!compact && <p className="label-brutal">Foto & IA</p>}
        <div className="flex flex-wrap gap-2">
          <input
            ref={inputRef}
            data-testid={`${testidPrefix}-photo-input`}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => onPickFile(e.target.files?.[0])}
          />
          <button type="button" data-testid={`${testidPrefix}-photo-btn`} onClick={() => inputRef.current?.click()} className={`btn-white ${btnHeight} text-sm`}>
            <Camera weight="bold" size={14} /> {photoUrl ? "Trocar" : "Foto"}
          </button>
          <button
            type="button"
            data-testid={`${testidPrefix}-analyze-btn`}
            onClick={analyze}
            disabled={!photoUrl || analyzing}
            className={`btn-pink ${btnHeight} text-sm disabled:opacity-60`}
          >
            <Sparkle weight="bold" size={14} /> {analyzing ? "Analisando…" : "IA"}
          </button>
          {photoUrl && (
            <button
              type="button"
              data-testid={`${testidPrefix}-photo-clear`}
              onClick={() => onPhoto("")}
              className={`btn-white ${btnHeight} text-sm`}
              title="Remover foto"
            >
              <X weight="bold" size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
